import { useEffect, useRef } from "react";
import "./ProphetsCosmicBackground.css";

/**
 * ProphetsCosmicBackground
 * ------------------------------------------------------------
 * Animated cosmic layer for the Prophets hero.
 *
 *   • Uses inline SVG + CSS animations only (no canvas, WebGL,
 *     Three.js, Spline, GIF, video, or npm packages).
 *   • Absolutely positioned INSIDE the hero <section>. It layers
 *     ABOVE the existing static hero SVG (which stays intact as a
 *     fallback / base) and BELOW the hero content (z-index: 0
 *     vs. content's z-10).
 *   • pointer-events: none — Mint / Buy on Marketplace buttons
 *     are never blocked.
 *   • Desktop-only parallax via a rAF-throttled mouse listener
 *     that writes two CSS variables (`--pcb-x`, `--pcb-y`) onto
 *     the component root. Touch / mobile / coarse-pointer devices
 *     never install the listener, and reduced-motion users get a
 *     fully static composition.
 *
 * ROLLBACK
 * ------------------------------------------------------------
 * The animated background is opt-in. To restore the exact original
 * static hero, either:
 *   (a) remove the `<ProphetsCosmicBackground />` line from
 *       ProphetsHero.jsx, OR
 *   (b) set `ENABLE_COSMIC_BG = false` below.
 * Either action returns the hero to the byte-identical static
 * background that shipped before this component was added.
 */

const ENABLE_COSMIC_BG = true;

/* Deterministic star positions in a 100×100 percentage grid so the
   composition looks the same on every reload. Marked stars with
   `mobileHide` are dropped at ≤767px to keep the density elegant. */
const STARS = [
  { x:  8, y: 14, r: 1.1, twinkle: true,  mobileHide: false },
  { x: 17, y:  9, r: 0.7, twinkle: false, mobileHide: true  },
  { x: 26, y: 22, r: 1.2, twinkle: true,  mobileHide: false },
  { x: 35, y: 12, r: 0.6, twinkle: false, mobileHide: true  },
  { x: 46, y:  6, r: 0.9, twinkle: true,  mobileHide: false },
  { x: 56, y: 18, r: 0.7, twinkle: false, mobileHide: true  },
  { x: 66, y:  9, r: 1.0, twinkle: true,  mobileHide: false },
  { x: 74, y: 20, r: 0.6, twinkle: false, mobileHide: true  },
  { x: 82, y: 12, r: 0.8, twinkle: true,  mobileHide: false },
  { x: 92, y:  8, r: 1.0, twinkle: false, mobileHide: true  },
  { x:  6, y: 78, r: 1.0, twinkle: true,  mobileHide: false },
  { x: 15, y: 88, r: 0.7, twinkle: false, mobileHide: true  },
  { x: 28, y: 82, r: 1.1, twinkle: true,  mobileHide: false },
  { x: 40, y: 90, r: 0.6, twinkle: false, mobileHide: true  },
  { x: 52, y: 86, r: 0.8, twinkle: true,  mobileHide: false },
  { x: 64, y: 92, r: 0.7, twinkle: false, mobileHide: true  },
  { x: 76, y: 82, r: 1.0, twinkle: true,  mobileHide: false },
  { x: 86, y: 88, r: 0.7, twinkle: false, mobileHide: true  },
  { x: 94, y: 78, r: 0.9, twinkle: true,  mobileHide: false },
  // Two prominent signal points near the widest orbit's apses.
  { x:  4, y: 50, r: 1.4, signal: true,   mobileHide: false },
  { x: 96, y: 50, r: 1.4, signal: true,   mobileHide: false },
];

export default function ProphetsCosmicBackground() {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!ENABLE_COSMIC_BG) return undefined;
    if (typeof window === "undefined") return undefined;

    const root = rootRef.current;
    if (!root) return undefined;

    // Only enable parallax on precise pointers (mouse) and non-reduced-motion.
    const fine = window.matchMedia && window.matchMedia("(pointer: fine)").matches;
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced) return undefined;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = 0;
    let ticking = false;

    const section = root.closest("section") || root.parentElement;
    if (!section) return undefined;

    const onMove = (event) => {
      const rect = section.getBoundingClientRect();
      // Normalize to -1 .. +1 relative to the section's centre.
      const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      // Clamp to a slight easing curve so the corners don't feel violent.
      targetX = Math.max(-1, Math.min(1, nx));
      targetY = Math.max(-1, Math.min(1, ny));
      if (!ticking) {
        ticking = true;
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      if (!ticking) {
        ticking = true;
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const tick = () => {
      // Smooth easing — never snap directly to the cursor.
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      root.style.setProperty("--pcb-x", currentX.toFixed(3));
      root.style.setProperty("--pcb-y", currentY.toFixed(3));
      if (Math.abs(currentX - targetX) > 0.001 || Math.abs(currentY - targetY) > 0.001) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        ticking = false;
      }
    };

    section.addEventListener("mousemove", onMove, { passive: true });
    section.addEventListener("mouseleave", onLeave, { passive: true });

    return () => {
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseleave", onLeave);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  if (!ENABLE_COSMIC_BG) return null;

  return (
    <div
      ref={rootRef}
      className="pcb-root"
      aria-hidden="true"
      data-testid="prophets-cosmic-bg"
    >
      {/* Layer 1 — central breathing halo */}
      <span className="pcb-halo" />

      {/* Layer 2 — six elliptical orbits, three CW / three CCW, all extremely slow */}
      <div className="pcb-orbits">
        <svg
          viewBox="0 0 1600 1000"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="pcb-stroke-a" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="rgba(210,220,240,0)" />
              <stop offset="50%"  stopColor="rgba(210,220,240,0.22)" />
              <stop offset="100%" stopColor="rgba(210,220,240,0)" />
            </linearGradient>
            <linearGradient id="pcb-stroke-b" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="rgba(150,165,200,0)" />
              <stop offset="50%"  stopColor="rgba(150,165,200,0.18)" />
              <stop offset="100%" stopColor="rgba(150,165,200,0)" />
            </linearGradient>
            <linearGradient id="pcb-stroke-c" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="rgba(140,150,200,0)" />
              <stop offset="50%"  stopColor="rgba(140,150,200,0.13)" />
              <stop offset="100%" stopColor="rgba(140,150,200,0)" />
            </linearGradient>
          </defs>
          {/* Six orbits, tilted slightly so the composition never feels static. */}
          <g className="pcb-orbit pcb-orbit--a">
            <ellipse cx="800" cy="500" rx="750" ry="210"
                     fill="none" stroke="url(#pcb-stroke-a)" strokeWidth="0.75" />
          </g>
          <g className="pcb-orbit pcb-orbit--b">
            <ellipse cx="800" cy="500" rx="640" ry="178"
                     transform="rotate(-8 800 500)"
                     fill="none" stroke="url(#pcb-stroke-b)" strokeWidth="0.7" opacity="0.9" />
          </g>
          <g className="pcb-orbit pcb-orbit--c">
            <ellipse cx="800" cy="500" rx="520" ry="145"
                     transform="rotate(6 800 500)"
                     fill="none" stroke="url(#pcb-stroke-a)" strokeWidth="0.65" opacity="0.85" />
          </g>
          <g className="pcb-orbit pcb-orbit--d">
            <ellipse cx="800" cy="500" rx="410" ry="118"
                     transform="rotate(-4 800 500)"
                     fill="none" stroke="url(#pcb-stroke-c)" strokeWidth="0.6"  opacity="0.9" />
          </g>
          <g className="pcb-orbit pcb-orbit--e">
            <ellipse cx="800" cy="500" rx="310" ry="88"
                     transform="rotate(10 800 500)"
                     fill="none" stroke="url(#pcb-stroke-b)" strokeWidth="0.55" opacity="0.85" />
          </g>
          <g className="pcb-orbit pcb-orbit--f">
            <ellipse cx="800" cy="500" rx="220" ry="62"
                     transform="rotate(-14 800 500)"
                     fill="none" stroke="url(#pcb-stroke-c)" strokeWidth="0.5"  opacity="0.8"
                     strokeDasharray="1 6" />
          </g>
        </svg>
      </div>

      {/* Layer 3 — tiny stars + two signal points */}
      <div className="pcb-stars">
        {STARS.map((s, i) => {
          const cls = [
            "pcb-star",
            s.twinkle ? "pcb-star--twinkle" : "",
            s.signal  ? "pcb-star--signal"  : "",
            s.mobileHide ? "pcb-star--mobile-hide" : "",
          ].filter(Boolean).join(" ");
          return (
            <span
              key={`pcb-star-${i}`}
              className={cls}
              style={{
                left: `${s.x}%`,
                top: `${s.y}%`,
                width: `${s.r * 2}px`,
                height: `${s.r * 2}px`,
                animationDelay: `${(i % 7) * 1.3}s`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
